import { useEffect, useState } from 'react';
import { isInPast } from '@asp/domain';
import { useDictionary } from '../i18n/context.js';
import { Stamp } from './Stamp.js';

// ---------------------------------------------------------------------------
// The reschedule confirmation (spec 9.4, 12.3). Opened by Task 6 after a
// drag, or from the entry detail's keyboard-accessible control -- never
// part of the calendar screen itself. Every reschedule must show, before
// save: the old instant and the new one, each in browser-local and UTC
// (four values, via `Stamp` -- design spec 21.1's own requirement, not
// hand-rolled formatting), plus an explicit statement that a schedule-only
// change preserves approval and is recorded as an audit event.
//
// Carrying the instant without a display round-trip (spec 12.3 / Task 2's
// carried-forward decision):
// `toIsoUtc` (packages/domain/src/schedule/utc.ts) deliberately truncates to
// whole seconds for display -- nobody wants "14:00:00.000Z" in a
// confirmation dialog. `normalizeToUtc` preserves milliseconds. A component
// that took the *rendered* text and re-parsed it back into a Date would
// silently confirm a different, truncated instant than the one actually
// saved -- exactly the bug Task 2 spent three rounds removing, reappearing
// through the display path. So `onConfirm` below is always called with the
// `to` Date prop itself, untouched -- never a value rebuilt from anything
// this component renders.
//
// Timezone-change detection (spec 12.3): there is no DOM event for "the
// device timezone changed" -- the brief is explicit that something has to
// poll or re-check. This polls `Intl.DateTimeFormat().resolvedOptions().timeZone`
// on a one-second interval. A laptop crossing timezones is a slow event by
// human standards (it takes a flight landing, or a manual OS change) --
// there is no responsiveness reason to check faster than once a second, and
// the check itself is a cheap Intl call, so the interval's cost is
// negligible even left running for as long as the panel stays open. An
// interval (rather than a focus/visibilitychange listener) also catches the
// case where the OS clock/zone changes while the tab stays focused and
// visible the whole time -- a listener tied to the tab regaining focus
// would miss that.
//
// `Stamp` (./Stamp.tsx) memoizes its formatted output on `[date, tz]`. If
// this component left `tz` unset, `Stamp` would read the *rendering*
// browser's default zone at format time -- but since neither `date` (a
// fixed string) nor an unset `tz` ever changes across a re-render, that
// memo would never see a changed dependency, and the displayed local time
// would silently keep showing the zone the panel opened in even after the
// zone actually changed. So `currentZone` state is threaded into both
// `Stamp`s' `tz` prop explicitly: when the poll updates `currentZone`, the
// memo dependency changes and the preview actually recalculates -- the
// half of spec 12.3 ("recalculates the preview") that a bare warning
// banner alone does not satisfy.
//
// The same poll tick also updates a `now` state (not just `currentZone`),
// because the past-target guard needs a re-render to notice time elapsing
// even when the zone never changes -- a target that was valid when the
// panel opened can still drift into the past while the operator reads it.
// ---------------------------------------------------------------------------

const currentTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const TZ_POLL_INTERVAL_MS = 1000;

export type RescheduleStatus =
  | { kind: 'blocked_past' }
  | { kind: 'needs_review'; zone: string }
  | { kind: 'ready' };

/**
 * Pure decision function behind the confirmation surface, exported for
 * direct testing (per Task 5's brief) without mounting React. A past
 * target always blocks, even if the browser timezone has also moved --
 * there is no point asking the operator to review a normalization for a
 * time that is going to be rejected anyway. Otherwise, any disagreement
 * between the zone the panel last had the operator review (`reviewedZone`,
 * which starts as the zone at open) and the zone right now (`currentZone`)
 * un-arms the confirmation until the operator reviews again.
 */
export const rescheduleStatus = (params: {
  to: Date;
  now: Date;
  currentZone: string;
  reviewedZone: string;
}): RescheduleStatus => {
  const { to, now, currentZone, reviewedZone } = params;
  if (isInPast(to, now)) return { kind: 'blocked_past' };
  if (currentZone !== reviewedZone) return { kind: 'needs_review', zone: currentZone };
  return { kind: 'ready' };
};

export type RescheduleConfirmProps = {
  /** The instant currently scheduled, before this change. */
  from: Date;
  /** The instant this reschedule would save. Passed to `onConfirm` unchanged. */
  to: Date;
  onConfirm: (to: Date) => void | Promise<void>;
  onCancel: () => void;
};

export const RescheduleConfirm = ({ from, to, onConfirm, onCancel }: RescheduleConfirmProps): React.JSX.Element => {
  const dict = useDictionary();
  const [currentZone, setCurrentZone] = useState(currentTimeZone);
  const [reviewedZone, setReviewedZone] = useState(currentZone);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const check = (): void => {
      const zone = currentTimeZone();
      setCurrentZone((prev) => (prev === zone ? prev : zone));
      // Also drives the past-target guard. `to` is a fixed instant, so
      // whether it has drifted into the past is purely a function of how
      // much wall-clock time has elapsed since the panel opened -- the
      // same 1s tick that watches the zone is the only thing that can
      // notice that elapsing while the panel just sits open with nothing
      // else triggering a re-render.
      setNow(new Date());
    };
    const interval = setInterval(check, TZ_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const status = rescheduleStatus({ to, now, currentZone, reviewedZone });

  const handleConfirm = (): void => {
    if (status.kind !== 'ready') return;
    void onConfirm(to);
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <h2 className="t-section">{dict.ui.rescheduleConfirm.title}</h2>
      </div>
      <div className="panel__body stack">
        <div className="stack-1" data-testid="reschedule-from">
          <span className="t-label">{dict.ui.rescheduleConfirm.fromLabel}</span>
          <Stamp instant={from.toISOString()} tz={currentZone} />
        </div>

        <div className="stack-1" data-testid="reschedule-to">
          <span className="t-label">{dict.ui.rescheduleConfirm.toLabel}</span>
          <Stamp instant={to.toISOString()} tz={currentZone} />
        </div>

        <div className="alert alert--ai">
          <div className="alert__body">{dict.ui.rescheduleConfirm.approvalPreserved}</div>
        </div>

        {status.kind === 'blocked_past' && (
          <div className="alert alert--danger">
            <div className="alert__body">{dict.ui.rescheduleConfirm.pastError}</div>
          </div>
        )}

        {status.kind === 'needs_review' && (
          <div className="alert alert--attention">
            <div className="stack-1">
              <div className="alert__body">{dict.ui.rescheduleConfirm.timezoneChanged(status.zone)}</div>
              <div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setReviewedZone(status.zone)}
                >
                  {dict.ui.rescheduleConfirm.reviewUpdatedTimes}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="row" style={{ gap: 'var(--sp-3)' }}>
          <button
            type="button"
            className="btn btn--primary"
            disabled={status.kind !== 'ready'}
            onClick={handleConfirm}
          >
            {dict.ui.rescheduleConfirm.confirm}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            {dict.ui.rescheduleConfirm.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};
